import { MapPin } from 'lucide-react';
import { useMemo } from 'react';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN =
  import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || import.meta.env.VITE_MAPBOX_TOKEN || '';

interface MapSectionProps {
  address: string;
  coordinates?: string;
}

export function MapSection({ address, coordinates }: MapSectionProps) {
  const position = useMemo(() => {
    if (coordinates) {
      const parts = coordinates.split(',').map((s) => parseFloat(s.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        if (Math.abs(parts[0]) <= 90) {
          return { lat: parts[0], lng: parts[1] };
        }
        return { lat: parts[1], lng: parts[0] };
      }
    }
    return null;
  }, [coordinates]);

  const canShowMap = MAPBOX_TOKEN && position;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
            <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Site Location</h3>
            <p className="text-sm text-muted-foreground">{address}</p>
          </div>
        </div>
      </div>
      <div className="h-[400px] relative">
        {canShowMap ? (
          <Map
            mapboxAccessToken={MAPBOX_TOKEN}
            initialViewState={{
              longitude: position.lng,
              latitude: position.lat,
              zoom: 14,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
          >
            <Marker longitude={position.lng} latitude={position.lat} color="#E84A2B" />
          </Map>
        ) : (
          <div className="h-full flex items-center justify-center bg-muted/30">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              {position ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    Map unavailable — Mapbox token not configured
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Coordinates: {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No coordinates available for this job
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
